import React, { useState } from "react";
import { PrimaryButton, SecondaryButton } from "@/components/Buttons.jsx";
import EmailEditorModal from "@/components/EmailEditorModal.jsx";
import EmailPreviewTile from "@/components/EmailPreviewTile.jsx";
import ToggleSwitch from "@/components/ToggleSwitch.jsx";

const CAMPAIGN_TYPES = [
  { value: "welcome", label: "Welcome" },
  { value: "cart_abandonment", label: "Cart Abandonment" },
  { value: "reengagement", label: "Re-engagement" },
  { value: "birthday", label: "Birthday" },
];

const DELAY_UNITS = [
  { value: "minutes", label: "Minutes" },
  { value: "hours", label: "Hours" },
  { value: "days", label: "Days" },
];

const getDefaultType = (initialData) => {
  if (initialData.type) return initialData.type;
  return "welcome";
};

const CampaignForm = ({ initialData = {}, onSave, onCancel }) => {
  const isEdit = Boolean(initialData.name);
  const [type, setType] = useState(getDefaultType(initialData));
  const [name, setName] = useState(initialData.name || "");
  const [description, setDescription] = useState(initialData.description || "");
  const [active, setActive] = useState(initialData.active !== undefined ? initialData.active : true);
  // Delay fields
  const [delayValue, setDelayValue] = useState(initialData.delay_value || "");
  const [delayUnit, setDelayUnit] = useState(
    initialData.delay_unit || "minutes"
  );
  // Email fields
  const [emailSubject, setEmailSubject] = useState(initialData.email?.subject || "");
  const [emailPreviewText, setEmailPreviewText] = useState(initialData.email?.preview_text || "");
  const [emailHTML, setEmailHTML] = useState(initialData.email?.html || "");
  const [emailDesign, setEmailDesign] = useState(initialData.email?.design || null);
  const [excludeHeader, setExcludeHeader] = useState(initialData.email?.exclude_header || false);
  const [showEmailEditorModal, setShowEmailEditorModal] = useState(false);

  const renderDelayField = (label, value, onChangeValue, unit, onChangeUnit, placeholder) => (
    <div>
      <label className="block text-slate-700 dark:text-slate-200 font-semibold mb-1">
        {label}
      </label>
      <div className="flex gap-2">
        <input
          type="number"
          className="form-input w-20"
          value={value}
          onChange={(e) => onChangeValue(e.target.value)}
          placeholder={placeholder}
        />
        <select
          className="form-select flex-1"
          value={unit}
          onChange={(e) => onChangeUnit(e.target.value)}
        >
          {DELAY_UNITS.map((delayUnit) => (
            <option key={delayUnit.value} value={delayUnit.value}>
              {delayUnit.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  const renderTypeFields = () => {
    const campaignSettings = {
      cart_abandonment: {
        title: "Cart Abandonment Settings",
        delayLabel: "Delay After Abandonment",
        placeholder: "30"
      },
      reengagement: {
        title: "Re-engagement Settings", 
        delayLabel: "Delay After Last Order",
        placeholder: "60"
      },
      birthday: {
        title: "Birthday Settings",
        delayLabel: "Send before birthday",
        placeholder: "7"
      },
      welcome: {
        title: "Welcome Settings"
      }
    };

    const settings = campaignSettings[type];
    if (!settings) return null;

    // Welcome campaigns don't have delay fields
    if (type === "welcome") {
      return (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
            {settings.title}
          </h3>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
          {settings.title}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderDelayField(
            settings.delayLabel,
            delayValue,
            setDelayValue,
            delayUnit,
            setDelayUnit,
            settings.placeholder
          )}
        </div>
      </div>
    );
  };

  const renderEmailContentPreview = () => (
    <EmailPreviewTile
      emailContent={emailHTML}
      emailDesign={emailDesign}
      title="Email Content"
      description="This is the content that will be sent to the customer."
      onEdit={() => setShowEmailEditorModal(true)}
      buttonText="Edit Email Content"
    />
  );

  const handleEmailSave = (data, fromEditor) => {
    setEmailHTML(data.html);
    setEmailDesign(data.design);
    
    // If editing an existing campaign, also submit the parent form
    if (isEdit) {
      handleFormSubmit(data.html, data.design, false);
    }
  };

  // Extract form submission logic to reusable function (similar to BroadcastForm)
  const handleFormSubmit = async (customEmailContent = null, customEmailDesign = null, shouldClose = true) => {
    // Generate payload with specified keys
    if(!emailSubject){
      dispatch(addToast({ type: "error", message: "Email subject is required", timer: 6 }));
      return;
    }
    const payload = {
      name,
      description,
      type,
      active,
      delay_value: parseInt(delayValue) || 0,
      delay_unit: delayUnit,
      email: {
        subject: emailSubject,
        preview_text: emailPreviewText,
        html: customEmailContent || emailHTML,
        design: customEmailDesign || emailDesign,
        exclude_header: excludeHeader,
      },
    };

    try {
      if (onSave) {
        await onSave(payload, shouldClose);
      }
    } catch (error) {
      // Error handling is done in the parent component
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-2xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold">
          {isEdit ? "Edit Campaign" : "New Campaign"}
        </h1>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleFormSubmit();
        }}
        className="space-y-6"
      >
        <div>
          <label className="block text-slate-700 dark:text-slate-200 font-semibold mb-1">
            Campaign Type
          </label>
          <select
            className="form-select w-full"
            value={type}
            disabled
            onChange={(e) => setType(e.target.value)}
          >
            {CAMPAIGN_TYPES.map((campaignType) => (
              <option key={campaignType.value} value={campaignType.value}>
                {campaignType.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-slate-700 dark:text-slate-200 font-semibold mb-1">
            Campaign Name
          </label>
          <input
            type="text"
            className="form-input w-full"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter campaign name"
          />
        </div>
        <div>
          <label className="block text-slate-700 dark:text-slate-200 font-semibold mb-1">
            Description
          </label>
          <textarea
            className="form-textarea w-full"
            rows="3"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter campaign description"
          />
        </div>
        <div>
          <label className="block text-slate-700 dark:text-slate-200 font-semibold mb-1">
            Email Subject
          </label>
          <input
            type="text"
            className="form-input w-full"
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
            placeholder="Enter email subject"
          />
        </div>
        <div>
          <label className="block text-slate-700 dark:text-slate-200 font-semibold mb-1">
            Email Preview Text
          </label>
          <input
            type="text"
            className="form-input w-full"
            value={emailPreviewText}
            onChange={(e) => setEmailPreviewText(e.target.value)}
            placeholder="Enter preview text (shown in email clients)"
          />
        </div>
        <div className="space-y-3 p-3 border border-slate-200 dark:border-slate-700 rounded bg-slate-50 dark:bg-slate-900/20">
          <div className="flex items-center gap-3">
            <ToggleSwitch
              checked={active}
              onChange={setActive}
            />
            <span className="text-slate-700 dark:text-slate-200">Active Campaign</span>
          </div>
          <div className="flex items-center gap-3">
            <ToggleSwitch
              checked={excludeHeader}
              onChange={setExcludeHeader}
            />
            <span className="text-slate-700 dark:text-slate-200">Do not include default email header</span>
          </div>
        </div>
        {renderTypeFields()}
        {renderEmailContentPreview()}
        <div className="flex gap-2 justify-end">
          <SecondaryButton type="button" onClick={onCancel}>
            Cancel
          </SecondaryButton>
          <PrimaryButton type="submit">
            {isEdit ? "Update Campaign" : "Create Campaign"}
          </PrimaryButton>
        </div>
      </form>

      {/* Email Editor Modal */}
      <EmailEditorModal
        isOpen={showEmailEditorModal}
        onClose={() => setShowEmailEditorModal(false)}
        onSave={handleEmailSave}
        initialDesign={emailDesign}
        title="Email Editor"
        templateType={type}
        // additionalMergeTags={{
        //   last_added_item_name: {
        //     name: "Last Added Item Name",
        //     value: "{contactfield=last_added_item_name}",
        //     sample: "Some Product Name",
        //   },
        //   last_added_item_image: {
        //     name: "Last Added Item Image",
        //     value: "{contactfield=last_added_item_image}",
        //     sample: "https://cdn.saucey.com/direct/web/crm-placehold-product.png",
        //   },
        //   last_shopping_store_url: {
        //     name: "URL of last shopped store",
        //     value: "{contactfield=last_shopping_store_url}",
        //     sample: "https://yourstore.com",
        //   },
        // }}
      />
    </div>
  );
};

export default CampaignForm;
